import { useDispatch, useSelector } from 'react-redux';

import frameSvg from 'assets/icons/frame.svg';
import bullhornSvg from 'assets/icons/bullhorn.svg';
import briefcaseSvg from 'assets/icons/briefcase.svg';
import environmentSvg from 'assets/icons/environment.svg';
import tagSvg from 'assets/icons/tag.svg';
import formatsSvg from 'assets/icons/formats.svg';
import locationSvg from 'assets/icons/location.svg';
import targetSvg from 'assets/icons/index.svg';
import calendarSvg from 'assets/icons/calendar.svg';
import mapSvg from 'assets/icons/map.svg';
import moneySvg from 'assets/icons/money.svg';
import visualUnitsSvg from 'assets/icons/visual-units.svg';
import flagSvg from 'assets/icons/flag.svg';
import penAndBrushSvg from 'assets/icons/creatives.svg';

import Auth from 'modules/Auth';
import { PermissionsEnum } from 'consts/permissions';
import { City, Country, County, PostCode, Street } from 'components/common/Deal/Location/Location.types';
import { DealStatus, RouteFrameCode } from 'components/common/types/Deal.types';
import { FileList } from 'components/common/types/index';
import Card from 'components/common/Card';
import { CardType } from 'components/common/Card/Card.types';
import Input from 'lib/Input';
import { InputSize } from 'components/common/types/Input.types';
import Environment from 'components/common/Deal/Environment';
import List from 'components/common/Deal/List';
import VisualUnits from 'components/common/Deal/VisualUnits';
import Tags from 'components/common/Deal/Tags';
import Formats from 'components/common/Deal/Formats';
import Location from 'components/common/Deal/Location';
import Target from 'components/common/Deal/Target';
import AssetsMap from 'components/common/AssetsMap';
import { Store } from 'components/common/types/Store.types';
import { changeDealCurrentLineData } from 'store/dealManagement/reducer';
import { links } from 'components/common/Deal/Panel';
import DealLineLevelCpm from 'components/pages/DealWithLines/common/Main/Deal/DealLineLevelCpm/DealLineLevelCpm';
import { getLineLevelCpmSettings } from 'components/pages/DealWithLines/common/Main/Deal/selectors';
import CpmFilter from 'components/pages/DealWithLines/common/Main/Line/CpmFilter/CpmFilter';
import { MediaOwner } from 'components/common/Deal/MediaOwner/MediaOwner';
import { FeatureFlags } from 'components/common/types/Features.types';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { cancelFunctions } from 'utils/cancelFunctions';
import { getIsReseller } from 'store/publisher/selectors';
import { getIsDV360AndPGAndDspSyncEnabled } from 'store/dealManagement/selectors';
import { getDealFormReadOnly } from 'store/dealManagement/selectors/getDealFormReadOnly';
import { getCurrentLineId } from 'store/dealManagement/selectors/getCurrentLineId';
import { getCurrentLineAvailability } from 'store/dealManagement/selectors/availabilityAssets';
import Schedule from './Schedule';
import VenueTaxonomy from './VenueTaxonomy';
import SelectCreativeType from './SelectCreativeType';

// eslint-disable-next-line sonarjs/cognitive-complexity
const Line = (): React.ReactElement => {
  const dispatch = useDispatch();

  const isReseller = useSelector(getIsReseller);
  const isDV360AndPGAndDspSyncEnabled = useSelector(getIsDV360AndPGAndDspSyncEnabled);
  const { isLineLevelCpmVisible, isLineLevelCpmEditable } = useSelector(getLineLevelCpmSettings);
  const isCpmCampaignLevel = useSelector((state: Store) => state.dealManagement.isCpmCampaignLevel);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const lineName = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.name);
  const visualUnitCodes = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.visualUnitCodes);
  const visualUnitFiles = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.visualUnitFiles);
  const countries = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.countries);
  const streets = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.streets);
  const cities = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.cities);
  const postCodes = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.postCodes);
  const counties = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.counties);
  const availability = useSelector(getCurrentLineAvailability);
  const lineId = useSelector(getCurrentLineId);

  const readOnly = useSelector(getDealFormReadOnly);
  const isPendingReservation = bookingStatusCode === DealStatus.PENDING_RESERVATION;
  const tagIsReadOnly = readOnly && !isPendingReservation;

  return (
    <div id="line-level">
      <Card
        icon={bullhornSvg}
        header="Deal line name"
        info="Name your deal line to simplify identification and filtering"
        cardType={CardType.NONE}
        dataTestId="line-name-card"
        mandatory
      >
        <div className="relative">
          <Input
            dataTestId="line-name-input"
            placeholder="Type here..."
            name="dealLineName"
            value={lineName}
            size={InputSize.MEDIUM}
            onChange={(name: string) => dispatch(changeDealCurrentLineData({ name }))}
            debounceTime={1000}
            isDisabled={readOnly && !isPendingReservation}
          />
          {lineId ? (
            <div className="absolute flex items-center top-0 px-2 py-1 right-0 max-w-64 border s border-neutral-300 bg-neutral-200 h-full overflow-hidden rounded-r-md">
              <span className="block body-sm w-full">
                Deal line ID: <span className="sub-header-sm">{lineId}</span>
              </span>
            </div>
          ) : null}
        </div>
      </Card>
      <Card
        icon={calendarSvg}
        header="Schedule"
        info="Select the active dates and duration for your deal line"
        id={links.schedule.id}
        dataTestId="line-schedule"
      >
        <Schedule />
      </Card>
      {isDV360AndPGAndDspSyncEnabled && (
        <Card
          icon={penAndBrushSvg}
          header="Creative type"
          info="Select your creative type"
          id={links.creativeType.id}
          dataTestId="creative-type-card"
          mandatory
        >
          <SelectCreativeType />
        </Card>
      )}
      {isReseller && (
        <Card
          icon={briefcaseSvg}
          header="Media owners"
          info="Select Media Owners in deal"
          id={links.mediaOwners.id}
          dataTestId="line-mediaOwners"
          mandatory
        >
          <MediaOwner />
        </Card>
      )}
      {!isReseller ? (
        <Card
          icon={environmentSvg}
          header="Environment"
          info="Select your target environment categories"
          id={links.environments.id}
          dataTestId="line-environment"
          mandatory
        >
          <Environment />
        </Card>
      ) : null}
      {isLineLevelCpmVisible ? (
        <Card
          icon={moneySvg}
          header="CPM pricing"
          info="Add a unique CPM for this deal line"
          id={links.cpm.id}
          mandatory={!isCpmCampaignLevel}
        >
          <DealLineLevelCpm isDisabled={(readOnly && !isPendingReservation) || !isLineLevelCpmEditable} />
        </Card>
      ) : null}
      {isReseller && (
        <Card
          icon={moneySvg}
          header="Frame CPM"
          info="Add a max frame CPM pricing"
          id={links.environments.id}
          dataTestId="line-environment"
          mandatory
        >
          <CpmFilter isDisabled={readOnly && !isPendingReservation} />
        </Card>
      )}
      {!isReseller ? (
        <Card
          icon={formatsSvg}
          header="Formats"
          info="Filter available inventory by frame format"
          id={links.formats.id}
          dataTestId="line-formats"
        >
          <Formats />
        </Card>
      ) : null}
      <Card
        icon={flagSvg}
        header="Venue taxonomy"
        info="Refine frame availability by specific venue types"
        id={links.listOfFrames.id}
        dataTestId="line-venue-taxonomy"
      >
        <VenueTaxonomy readOnly={readOnly && !isPendingReservation} cancelFunctions={cancelFunctions} />
      </Card>
      <Card
        icon={frameSvg}
        header="Frames"
        info="Include or exclude specific frames for this deal line"
        id={links.listOfFrames.id}
        dataTestId="line-frames"
      >
        <List />
      </Card>
      {useHasFeatureAccess(FeatureFlags.VISUAL_UNITS) ? (
        <Card
          icon={visualUnitsSvg}
          header="Visual units"
          id={links.visualUnits.id}
          info="Select visual unit codes individually or via upload to run across these groups of frames"
          dataTestId="line-visual-units"
        >
          <VisualUnits
            isReadOnly={readOnly && !isPendingReservation}
            visualUnitCodes={visualUnitCodes}
            onChangeVisualUnitCodes={(codes: RouteFrameCode[]) =>
              dispatch(
                changeDealCurrentLineData({
                  visualUnitCodes: codes.map((unit) => ({ ...unit, name: unit.name ?? unit.code })),
                }),
              )
            }
            visualUnitFiles={visualUnitFiles}
            onChangeVisualUnitFiles={(files: FileList[]) =>
              dispatch(changeDealCurrentLineData({ visualUnitFiles: files }))
            }
          />
        </Card>
      ) : null}
      <Card
        icon={locationSvg}
        header="Location"
        info="Manage and refine target locations and areas"
        id={links.location.id}
        dataTestId="line-location"
      >
        <Location
          onChangeCountries={(newCountries: Country[]) =>
            dispatch(changeDealCurrentLineData({ countries: newCountries }))
          }
          countries={countries}
          streets={streets}
          onChangeStreets={(newStreets: Street[]) => dispatch(changeDealCurrentLineData({ streets: newStreets }))}
          cities={cities}
          onChangeCities={(newCities: City[]) => dispatch(changeDealCurrentLineData({ cities: newCities }))}
          postCodes={postCodes}
          onChangePostCodes={(newPostCodes: PostCode[]) =>
            dispatch(changeDealCurrentLineData({ postCodes: newPostCodes }))
          }
          counties={counties}
          onChangeCounties={(newCounties: County[]) => dispatch(changeDealCurrentLineData({ counties: newCounties }))}
        />
      </Card>
      <Card
        icon={targetSvg}
        header="Target"
        info="Input your delivery targets for this deal line"
        id={links.target.id}
        dataTestId="line-target"
      >
        <Target />
      </Card>
      {Auth.hasPermission(PermissionsEnum.DEAL_TAGS_VIEW) ? (
        <Card
          icon={tagSvg}
          header="Tags"
          info="Apply deal tags to filter frames by custom categories"
          id={links.tags.id}
          dataTestId="line-tags"
        >
          <Tags readOnly={tagIsReadOnly} />
        </Card>
      ) : null}
      {useHasFeatureAccess(FeatureFlags.GOOGLEMAPS) ? (
        <Card
          icon={mapSvg}
          header="Map"
          info="Review the final distribution of frames by location"
          id={links.map.id}
          dataTestId="line-assets-map"
        >
          <div className="h-125">
            <AssetsMap assets={availability.assets} />
          </div>
        </Card>
      ) : null}
    </div>
  );
};

export default Line;
